/* eslint-disable react/display-name */
import React, { forwardRef, useEffect, useState, useCallback } from 'react'
import { createUseStyles } from 'react-jss'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import _ from 'lodash'

import { loggingEnabled } from '../../../../utils/logging'

const useStyles = createUseStyles(theme => ({
  dateTimeSelect: {
    alignItems: 'center',
    border: 'none',
    backgroundColor: theme.colors.white,
    color: theme.colors.blue,
    cursor: 'pointer',
    display: 'flex',
    padding: '7px 11px',
    borderRadius: theme.borderRadius,
    // justifyContent: "center",
    justifyContent: 'flex-start',
    minWidth: 250,
    '&:active': {
      outline: 'none',
    },
    '&:focus': {
      outline: 'none',
    },
    '&[disabled] .text': {
      color: 'lightgrey',
    },
    '& .date': {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
    },
    '& .text': {
      fontFamily: 'AvenirNextLTPro',
      fontWeight: 400,
      fontSize: 13,
      fontWeight: 'normal',
      position: 'relative',
      bottom: 0,
      color: theme.colors.brushedsteel,
    },
  },
  time: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  calendar: {
    marginRight: 11,
    position: 'relative',
    bottom: -1,
  },
  clock: {
    marginRight: 10,
    position: 'relative',
    bottom: -1,
  },

  spacer: {
    display: 'block',
    content: '',
    height: 18,
    width: 1,
    backgroundColor: theme.colors.brushedsteel,
    marginLeft: 11,
    marginRight: 18,
  },
  modifiers: {
    fontFamily: 'TTRounds',
    fontWeight: 400,
    borderRadius: theme.borderRadius,
    border: 'none',
    boxShadow: '0 5px 11px 0 rgba(0,0,0,0.08)',
    '& .react-datepicker__header': {
      background: 'white',
      borderBottom: 'none',
    },
    '& .react-datepicker__current-month, .react-datepicker-time__header, .react-datepicker-year-header': {
      color: theme.colors.blue,
      fontSize: 16,
      fontWeight: 'bold',
      letterSpacing: '.62px',
      lineHeight: '19px',
    },
    '& .react-datepicker__current-month, .react-datepicker-time__header': {
      lineHeight: '31px',
      marginBottom: 5,
    },
    '& .react-datepicker__navigation': {
      top: 16,
    },
    '& .react-datepicker__navigation--next': {
      borderLeftColor: theme.colors.deepsea,
    },
    '& .react-datepicker__navigation--previous': {
      borderRightColor: theme.colors.deepsea,
    },
    '& .react-datepicker__month-container': {
      paddingRight: 5,
    },
    '& .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button)': {
      right: 100,
    },
    '& .react-datepicker__day, .react-datepicker__month-text, .react-datepicker__quarter-text': {
      // color: "#C8C8C8"
      color: '#000',
    },
    '& .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item': {
      // color: "#C8C8C8",
      color: '#000',
      height: 20,
    },
    '& .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--disabled': {
      // color: "#C8C8C8",
      color: '#CCC',
    },
    '& .react-datepicker__day--disabled, .react-datepicker__month-text--disabled, .react-datepicker__quarter-text--disabled, .react-datepicker__year-text--disabled': {
      color: '#C8C8C8',
    },
    '& .react-datepicker__day--selected:hover, .react-datepicker__day--in-selecting-range:hover, .react-datepicker__day--in-range:hover, .react-datepicker__month-text--selected:hover, .react-datepicker__month-text--in-selecting-range:hover, .react-datepicker__month-text--in-range:hover, .react-datepicker__quarter-text--selected:hover, .react-datepicker__quarter-text--in-selecting-range:hover, .react-datepicker__quarter-text--in-range:hover': {
      background: theme.colors.sunflower,
      color: theme.colors.coal,
    },
    '& .react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--in-range, .react-datepicker__month-text--selected, .react-datepicker__month-text--in-selecting-range, .react-datepicker__month-text--in-range, .react-datepicker__quarter-text--selected, .react-datepicker__quarter-text--in-selecting-range, .react-datepicker__quarter-text--in-range': {
      borderRadius: 0,
      background: theme.colors.sunflower,
      color: theme.colors.coal,
    },
    '& .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected': {
      background: theme.colors.sunflower,
      color: theme.colors.coal,
    },
    '& .react-datepicker__day-name': {
      color: theme.colors.stormcloud,
      fontSize: 14,
      lineHeight: '16px',
      letterSpacing: '0.54px',
      fontWeight: 300,
    },
  },
}))

const getLocationTimes = (locationHours, date) => {
  if (loggingEnabled) console.log({ getLocationTimes: date })
  if (!date) date = new Date()
  // Get hours of selected day of the week
  const selectedDay = locationHours.find(
    ({ day }) =>
      day ===
      moment(date)
        .format('dddd')
        .toLowerCase(),
  )
  if (typeof selectedDay === 'undefined' || !selectedDay) return false

  // Get hours of previous day, to account for closing hours after midnight from previous date
  const previousDay = locationHours.find(
    ({ day }) =>
      day ===
      moment(date)
        .subtract(1, 'd')
        .format('dddd')
        .toLowerCase(),
  )

  const hours = {
    open: {
      hour: parseInt(selectedDay.opening_time.split(':')[0]),
      min: parseInt(selectedDay.opening_time.split(':')[1]),
    },
    close: {
      hour: parseInt(selectedDay.closing_time.split(':')[0]),
      min: parseInt(selectedDay.closing_time.split(':')[1]),
    },
  }

  const actualOpenTime = moment(date)
    .hour(hours.open.hour)
    .minute(hours.open.min)

  const actualCloseTime =
    typeof previousDay !== 'undefined' && previousDay.closing_time.split(':')[0] < hours.open.hour
      ? moment(date)
          .hour(previousDay.closing_time.split(':')[0])
          .minute(previousDay.closing_time.split(':')[1])
      : moment(date)
          .hour(hours.close.hour)
          .minute(hours.close.min)

  return {
    opening: {
      hour: hours.open.hour,
      min: hours.open.min,
    },
    closing: {
      hour: hours.close.hour,
      min: hours.close.min,
    },
    actualOpenTime: new Date(actualOpenTime.second(0).millisecond(0)),
    actualCloseTime: new Date(actualCloseTime.second(0).millisecond(0)),
  }
}

const DateTimeSelect = props => {
  const classes = useStyles()
  const initialMaxDate = new Date(
    new Date(props.minDate).setFullYear(new Date(props.minDate).getFullYear() + 1),
  )
  const [minTime, setMinTime] = useState(props.minDate !== null ? new Date(props.minDate) : null)
  const [maxTime, setMaxTime] = useState(initialMaxDate)
  const [excludeTimes, setExcludeTimes] = useState(null)
  const [selectedDateTime, setSelectedDateTime] = useState(props.date)
  const [locationTimes, setLocationTimes] = useState(null)
  const [disableInput, setDisableInput] = useState(true)
  const [placeholderText, setPlaceholderText] = useState(
    props.locationType == 'pickup'
      ? 'Please select a location'
      : 'Please select location & pickup time',
  )

  useEffect(() => setMinTime(props.minDate), [props.minDate])
  useEffect(() => setSelectedDateTime(props.date), [props.date])

  useEffect(() => {
    setDisableInput(props.location?.hours !== null ? props.minDate === null : true)
    if (props.locationType == 'pickup' && props.location.hours)
      setPlaceholderText('Select a date and time')
    else if (props.locationType == 'dropoff' && props.location.hours && props.minDate)
      setPlaceholderText('Select a date and time')
    else
      setPlaceholderText(
        props.locationType == 'pickup'
          ? 'Please select a location'
          : 'Please select location & pickup time',
      )
  }, [props])

  useEffect(() => {
    if (props.location.value === '') return
    if (loggingEnabled)
      console.log(`useEffect ${props.locationType} [props.location, selectedDateTime]`)
    setupTimeSelect()
  }, [props.location, props.minDate, setupTimeSelect])

  const setupTimeSelect = useCallback((providedDate = null) => {
    if (props.location.hours == null) return

    if (loggingEnabled) console.log('[DateTimeSelect.js]: setupTimeSelect')
    // Sets excluded times and no min time if previous day closing time is after midnight,
    // otherwise sets min/max times based on current day's opening hours

    const selectedDate = selectedDateTime
      ? selectedDateTime
      : providedDate
      ? providedDate
      : new Date()

    const times = getLocationTimes(props.location.hours, selectedDate)
    setLocationTimes(times)

    // Set maxTime to location closing hours 1 year in the future
    const newMaxTime = new Date(times.actualCloseTime)
    newMaxTime.setFullYear(newMaxTime.getFullYear())
    if (times.closing.hour == '00' || times.closing.hour == '24') {
      newMaxTime.setHours(23, 30, 0)
    } else {
      newMaxTime.setHours(times.closing.hour, times.closing.min, 0)
    }

    if (times.actualCloseTime < times.actualOpenTime) {
      const closingTime = new Date(times.actualCloseTime)

      let excludedTimes = []
      const loopIterations =
        (times.opening.hour * 60 + times.opening.min) / 30 -
        (times.closing.hour * 60 + times.closing.min) / 30

      for (let i = 1; i < loopIterations; i++) {
        let exclude = moment(closingTime).add(30 * i, 'm')
        excludedTimes.push(new Date(exclude))
      }
      setMinTime(new Date(props.minDate).setHours(0, 0, 0, 0))
      setMaxTime(newMaxTime)
      setExcludeTimes(excludedTimes)
    } else {
      // Set minDate/minTime to location opening hours
      if (loggingEnabled)
        console.log({
          minDate: moment(props.minDate).format('L'),
          selectedDate: moment(selectedDate).format('L'),
        })

      if (selectedDate && moment(props.minDate).format('L') != moment(selectedDate).format('L')) {
        let newMinTime = new Date(props.minDate)
        const hr = moment(times.actualOpenTime).hours()
        const min = moment(times.actualOpenTime).minutes()
        newMinTime.setHours(hr, min, 0, 0)
        setMinTime(newMinTime)
      } else setMinTime(props.minDate)

      setMaxTime(newMaxTime)
      setExcludeTimes(null)
    }
  })

  const setDate = date => {
    setupTimeSelect(date) // Update opening times based on changed date so logic runs correctly below
    let newDate = moment(date)
    // if (loggingEnabled) console.log({
    //   setMinTime: moment(props.minDate) < newDate,
    //   newDate: newDate,
    //   minDate: props.minDate,
    //   locationTimes: locationTimes,
    //   today: moment(newDate).format('dddd').toLowerCase(),
    //   hours: props.location.hours,
    // })

    const today = props.location.hours.find(
      i =>
        i.day ==
        moment(newDate)
          .format('dddd')
          .toLowerCase(),
    )
    const minTime =
      moment(props.minDate) < newDate
        ? { hour: today?.opening_time.split(':')[0], min: today?.opening_time.split(':')[1] }
        : { hour: moment(props.minDate).hour(), min: moment(props.minDate).minute() }

    if (
      (!excludeTimes && newDate.hour() < minTime.hour) ||
      (excludeTimes &&
        newDate.isBetween(locationTimes?.actualCloseTime, locationTimes?.actualOpenTime))
    )
      newDate.hour(minTime.hour).minute(minTime.min)

    setSelectedDateTime(newDate.toDate())
    props.onDateChange(newDate.toDate())
  }

  const disableDays = date => {
    const { location } = props
    if (location === null || location.hours === null) return false

    const day = moment(date)
      .format('dddd')
      .toLowerCase()
    const disable = location?.hours?.map(({ day }) => day)
    return disable.includes(day)
  }

  const CustomInput = forwardRef(({ value, onClick, placeholderText }, ref) => (
    <button onClick={onClick} className={classes.dateTimeSelect} disabled={disableInput}>
      <span className="date">
        <span className="text">
          {value ? value : placeholderText ? placeholderText : 'Select a date and time'}
        </span>
      </span>
    </button>
  ))

  return (
    <DatePicker
      onChange={date => setDate(date)}
      calendarClassName={classes.modifiers}
      name={props.name}
      filterDate={disableDays}
      selected={selectedDateTime}
      minDate={minTime}
      minTime={minTime}
      maxTime={maxTime}
      excludeTimes={excludeTimes}
      showTimeSelect
      disabledKeyboardNavigation
      useShortMonthInDropdown
      disabled={disableInput}
      dateFormat="d MMMM yyyy | h:mm aa"
      timeFormat="HH:mm"
      timeIntervals={30}
      timeCaption="Time"
      customInput={<CustomInput placeholderText={placeholderText} />}
    />
  )
}

export default DateTimeSelect
